import { createFileRoute, Link } from "@tanstack/react-router";

import { ContactForm } from "../components/home/ContactForm";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { Reviews } from "../components/shared/Reviews";
import { Team } from "../components/shared/Team";
import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { Reveal } from "../components/site/Reveal";
import { ServicesEcosystem } from "../components/uslugi/ServicesEcosystem";
import { ServiceGlyph } from "../components/uslugi/ServiceGlyph";
import { serviceCatalog } from "../lib/services-catalog";
import { SITE_URL, breadcrumbJsonLd, buildMeta } from "../lib/site";
import { crumbTuples } from "../lib/breadcrumbs";

const title = "Услуги — разработка сайтов, реклама, SEO и CRM | hay.agency";
const description =
  "Услуги hay.agency: разработка сайтов и интернет-магазинов, SEO-продвижение, Яндекс Директ, реклама в картах, внедрение amoCRM, RetailCRM и Битрикс24.";

export const Route = createFileRoute("/uslugi")({
  head: () => ({
    ...buildMeta({
      title,
      description,
      path: "/uslugi",
      image: `${SITE_URL}/og/uslugi.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Услуги hay.agency",
      locale: "ru_RU",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${SITE_URL}/uslugi#page`,
              url: `${SITE_URL}/uslugi`,
              name: title,
              description,
              isPartOf: { "@id": `${SITE_URL}/#website` },
            },
            breadcrumbJsonLd(crumbTuples("/uslugi"), { inGraph: true }),
            {
              "@type": "ItemList",
              name: "Услуги hay.agency",
              itemListElement: serviceCatalog
                .flatMap((group) => group.items)
                .map((item, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: item.title,
                  url: `${SITE_URL}${item.to}`,
                })),
            },
          ],
        }),
      },
    ],
  }),
  component: UslugiPage,
});

function UslugiPage() {
  return (
    <ServicePageLayout breadcrumb="Услуги" jsonLd={false}>
      <section className="mx-auto px-5 pt-6 lg:px-[21px] lg:pt-[32px]">
        <Reveal priority className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:items-end">
          <h1 className="section-title">Услуги</h1>
          <p className="body-12 text-muted-foreground">
            Разработка сайтов, интернет-маркетинг и CRM — в одной команде.
            Собираем систему привлечения и продаж: от сайта и рекламы
            до отдела продаж и аналитики. Подключаем ИИ к аналитике,
            маркетингу, разработке и автоматизации там, где это даёт
            измеримый эффект.
          </p>

        </Reveal>
        <Reveal priority delay={80}>
          <a
            href="#event"
            className="mt-6 inline-flex items-center justify-center bg-brand px-6 py-4 text-[14px] leading-[16px] text-brand-foreground transition-opacity duration-300 hover:opacity-80 lg:mt-[30px]"
          >
            Обсудить задачу
          </a>
        </Reveal>
      </section>

      <section className="mx-auto px-5 pt-12 lg:px-[21px] lg:pt-[90px]">
        <h2 className="sr-only">Направления работы</h2>
        <div className="flex flex-col gap-12 lg:gap-[90px]">
          {serviceCatalog.map((group) => (
            <div key={group.id}>
              <Reveal>
                <h2 className="section-title">{group.label}</h2>
              </Reveal>
              <div className="mt-6 grid gap-[2px] bg-border lg:mt-[34px] lg:grid-cols-3">
                {group.items.map((item) => (
                  <Reveal key={item.to} className="bg-background">
                    <div className="flex h-full flex-col bg-secondary/40 transition-colors duration-300 lg:min-h-[300px]">
                      <Link
                        to={item.to}
                        className="group flex flex-1 flex-col p-5 lg:p-[30px]"
                      >
                        <div className="lg:flex lg:items-start lg:justify-between lg:gap-4">
                          <h3 className="text-[20px] font-medium leading-[24px] tracking-[-0.6px] lg:text-[24px] lg:leading-[28px] lg:tracking-[-0.9px]">
                            {item.title}
                          </h3>
                          <ServiceGlyph to={item.to} />
                        </div>
                        <p className="body-14 mt-3 text-muted-foreground lg:mt-[14px]">
                          {item.text}
                        </p>
                        <span className="mt-6 inline-flex items-center text-[14px] leading-[16px] text-brand-text underline decoration-[1px] underline-offset-[5px] lg:mt-auto lg:pt-[24px]">
                          {item.linkLabel}
                        </span>
                      </Link>
                      {item.sublinks ? (
                        <ul className="flex flex-wrap gap-x-5 gap-y-2 px-5 pb-5 lg:px-[30px] lg:pb-[30px]">
                          {item.sublinks.map((sub) => (
                            <li key={sub.to}>
                              <Link
                                to={sub.to}
                                className="text-[13px] leading-[16px] text-muted-foreground underline decoration-[1px] underline-offset-[4px] transition-colors duration-300 hover:text-brand-text"
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ServicesEcosystem />

      <div className="pt-12 lg:pt-[90px]">
        <ClientsMarquee bare />
      </div>

      <Reviews />
      <Team />
      <ContactForm />
    </ServicePageLayout>
  );
}
